import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getRemoteAreaScheduleTimeline } from '../../components/Remote/deviceRemoteApi';

const LABEL_WIDTH = 138;
const HOUR_WIDTH = 76;
const DAY_HOURS = 24;
const TIMELINE_WIDTH = HOUR_WIDTH * DAY_HOURS;

function getDayRange() {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { start, end };
}

function getBlockLayout(startValue, endValue, rangeStart, rangeEnd) {
  const start = new Date(startValue).getTime();
  const end = new Date(endValue).getTime();
  const rangeStartMs = rangeStart.getTime();
  const rangeEndMs = rangeEnd.getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= rangeStartMs || start >= rangeEndMs) {
    return null;
  }
  const duration = rangeEndMs - rangeStartMs;
  const clippedStart = Math.max(start, rangeStartMs);
  const clippedEnd = Math.min(end, rangeEndMs);
  return {
    left: ((clippedStart - rangeStartMs) / duration) * TIMELINE_WIDTH,
    width: Math.max(28, ((clippedEnd - clippedStart) / duration) * TIMELINE_WIDTH),
  };
}

function TimelineBlock({ item, rangeStart, rangeEnd, kind }) {
  const startValue = item.startTime || item.startsAtUtc;
  const endValue = item.endTime || item.endsAtUtc;
  const layout = getBlockLayout(startValue, endValue, rangeStart, rangeEnd);
  if (!layout) return null;
  const title = item.label || item.title || 'Scheduled item';
  const start = new Date(startValue);
  const end = new Date(endValue);
  return (
    <div
      className={`remote-schedule__block remote-schedule__block--${kind}`}
      style={{ left: layout.left, width: layout.width }}
      title={`${title} ${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}-${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
    >
      <strong>{title}</strong>
      <span>
        {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        {' - '}
        {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
    </div>
  );
}

export default function RemoteScheduleTimeline({ areaId, enabled }) {
  const viewportRef = useRef(null);
  const [status, setStatus] = useState('idle');
  const [data, setData] = useState({ devices: [], items: [], gigs: [] });
  const [message, setMessage] = useState('');
  const range = useMemo(() => getDayRange(), [enabled]);

  useEffect(() => {
    if (!enabled || !areaId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        setStatus((current) => current === 'ready' ? 'refreshing' : 'loading');
        const result = await getRemoteAreaScheduleTimeline({
          areaId,
          start: range.start.toISOString(),
          end: range.end.toISOString(),
        });
        if (cancelled) return;
        setData({
          devices: Array.isArray(result.devices) ? result.devices : [],
          items: Array.isArray(result.items) ? result.items : [],
          gigs: Array.isArray(result.gigs) ? result.gigs : [],
        });
        setMessage('');
        setStatus('ready');
      } catch (error) {
        if (cancelled) return;
        setMessage(error.message || 'Failed to load schedule.');
        setStatus('error');
      }
    };
    load();
    const timer = window.setInterval(load, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [areaId, enabled, range.end, range.start]);

  useEffect(() => {
    if (!enabled || status !== 'ready' || !viewportRef.current) return;
    const elapsed = Date.now() - range.start.getTime();
    const dayDuration = range.end.getTime() - range.start.getTime();
    const nowX = Math.max(0, Math.min(TIMELINE_WIDTH, (elapsed / dayDuration) * TIMELINE_WIDTH));
    viewportRef.current.scrollLeft = Math.max(0, LABEL_WIDTH + nowX - window.innerWidth * 0.35);
  }, [enabled, range.end, range.start, status]);

  const itemsByDevice = useMemo(() => {
    const grouped = new Map();
    data.items.forEach((item) => {
      const id = Number(item.deviceId);
      if (!grouped.has(id)) grouped.set(id, []);
      grouped.get(id).push(item);
    });
    return grouped;
  }, [data.items]);

  const nowLeft = ((Date.now() - range.start.getTime())
    / (range.end.getTime() - range.start.getTime())) * TIMELINE_WIDTH;

  return (
    <div className="remote-schedule" ref={viewportRef}>
      <div
        className="remote-schedule__canvas"
        style={{ width: LABEL_WIDTH + TIMELINE_WIDTH }}
      >
        <div className="remote-schedule__corner" style={{ width: LABEL_WIDTH }}>Devices &amp; Gigs</div>
        <div
          className="remote-schedule__hours"
          style={{ width: TIMELINE_WIDTH }}
        >
          {Array.from({ length: DAY_HOURS }, (_, hour) => (
            <div key={hour} style={{ width: HOUR_WIDTH }}>
              {String(hour).padStart(2, '0')}:00
            </div>
          ))}
        </div>

        <div className="remote-schedule__rows">
          <div className="remote-schedule__row">
            <div className="remote-schedule__label" style={{ width: LABEL_WIDTH }}>
              <strong>Gigs</strong>
              <span>{data.gigs.length} today</span>
            </div>
            <div className="remote-schedule__lane" style={{ width: TIMELINE_WIDTH }}>
              {data.gigs.map((gig) => (
                <TimelineBlock
                  key={gig.id}
                  item={gig}
                  rangeStart={range.start}
                  rangeEnd={range.end}
                  kind="gig"
                />
              ))}
            </div>
          </div>
          {data.devices.map((device) => (
            <div className="remote-schedule__row" key={device.id}>
              <div className="remote-schedule__label" style={{ width: LABEL_WIDTH }}>
                <strong>{device.name}</strong>
                <span>{device.type}</span>
              </div>
              <div className="remote-schedule__lane" style={{ width: TIMELINE_WIDTH }}>
                {(itemsByDevice.get(Number(device.id)) || []).map((item) => (
                  <TimelineBlock
                    key={item.occurrenceKey || item.id}
                    item={item}
                    rangeStart={range.start}
                    rangeEnd={range.end}
                    kind={item.type || 'item'}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        {nowLeft >= 0 && nowLeft <= TIMELINE_WIDTH ? (
          <div
            className="remote-schedule__now"
            style={{ left: LABEL_WIDTH + nowLeft }}
          />
        ) : null}
        {status === 'loading' ? <div className="remote-schedule__status">Loading schedule...</div> : null}
        {status === 'error' ? <div className="remote-schedule__status">{message}</div> : null}
      </div>
    </div>
  );
}
